import React from "react";
import ConfirmActionDialog from "./ConfirmActionDialog";
import { useRacketLabAdminT } from "../../i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, TICKET_ACTION_LABEL_KEY, labelKeyFor } from "../../i18n/racketLabMaps";

/**
 * TicketStatusStepper — Sub-sprint B (RL-FRONTEND-SHELL-V0) +
 * SS-1 (Feb 2026) lifecycle FE↔BE alignment.
 *
 * Renders current ticket status + the actions allowed from that status.
 * SS-1: the allowed actions are now driven by the backend endpoint
 * `GET /admin/racket-lab/tickets/{id}/allowed-actions` (passed in via
 * the `allowedActions` prop). The internal `ALLOWED_ACTIONS_FALLBACK`
 * map matches the RL-A.6 canonical enum and is used only when the
 * parent did not (or could not) fetch the allowed-actions payload.
 *
 * Each click POSTs to the per-action backend endpoint. Action SLUGs
 * (`check-in`, `assign-operator`, …) remain the technical contract
 * and drive both API URLs and `data-testid` attributes.
 *
 * QUICK 1.C i18n migration: current status, terminal copy, button
 * labels, confirm titles/descriptions resolved via
 * `racketLab.ticket_actions.*` / `racketLab.ticket_status.*`.
 */
// RL-A.6 canonical fallback. Backend `ACTIONS_BY_STATUS` (routes.py)
// is the authoritative source — kept here only as a safety net.
const ALLOWED_ACTIONS_FALLBACK = {
  draft: ["check-in", "assign-operator", "assign-machine", "cancel"],
  checked_in: ["assign-operator", "assign-machine", "start", "cancel"],
  assigned: ["assign-operator", "assign-machine", "start", "cancel"],
  in_progress: ["assign-machine", "request-approval", "finish", "cancel"],
  pending_customer_approval: ["mark-approved", "start", "cancel"],
  ready_for_pickup: ["pickup", "cancel"],
  closed: [],
  cancelled: [],
  deleted: [],
};

export const TicketStatusStepper = ({ ticket, onAction, busy, allowedActions }) => {
  const t = useRacketLabAdminT();
  const status = ticket?.status || "draft";
  // Prefer backend-supplied list; fall back to RL-A.6 canonical map.
  const actions = Array.isArray(allowedActions)
    ? allowedActions
    : (ALLOWED_ACTIONS_FALLBACK[status] || []);

  return (
    <div data-testid="rl-ticket-stepper" className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{t("ticket_actions.current_status_label")}</p>
          <p data-testid="rl-ticket-current-status" className="text-lg font-semibold text-slate-900">
            {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, status))}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {actions.length === 0 && (
          <p data-testid="rl-ticket-no-actions" className="text-xs text-slate-500">
            {t("ticket_actions.terminal_state")}
          </p>
        )}
        {actions.map((act) => {
          const isCancel = act === "cancel";
          const actionLabel = t(labelKeyFor(TICKET_ACTION_LABEL_KEY, act));
          return (
            <ConfirmActionDialog
              key={act}
              tone={isCancel ? "danger" : "default"}
              title={t("ticket_actions.confirm_title", { action: actionLabel })}
              description={t("ticket_actions.confirm_description", { action: actionLabel, ticketId: ticket?.id?.slice(0, 18) || "" })}
              confirmLabel={actionLabel}
              onConfirm={() => onAction(act)}
              disabled={!!busy}
              dataTestId={`rl-ticket-action-${act}`}
              trigger={
                <button
                  type="button"
                  disabled={!!busy}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                    isCancel
                      ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
                      : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  }`}
                >
                  {actionLabel}
                </button>
              }
            />
          );
        })}
      </div>
    </div>
  );
};

export default TicketStatusStepper;
